import {
  KalturaAssetListResponse,
  KalturaFilterPager,
  KalturaProgramAsset,
  KalturaSearchAssetFilter,
} from '@kaltura-ott/ott-client-types/api/types';
import { EMPTY, from, Observable, of, Subject, zip } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';

import { KuxProgramType, MAX_EPG_PERIOD } from '../interfaces';

import { getAssetList, getAssetsRecursively } from './asset';
import { assetToProgram } from './asset-v2.helper';
import { PERFORMANCE_FEATURE } from './config';
import { aggregateEpgByDay, getEpgCacheDays, setEpgCacheDays } from './epg-cache-v2';
import { getNotFoundProgramType } from './epg-cache.helper';
import {
  epgArgsToKsql,
  epgArgsToKsqlProgramLeft,
  epgArgsToKsqlProgramRight,
  epgArgsToKsqlSingle,
  epgMosaicArgsToKsql,
  epgMosaicArgsToKsqlSingle,
  EpgRequestArgs,
} from './epg-request';
import { cloneObject } from './utils/common';
import { startOfDay } from './utils/date';

export const getMosaicPrograms = (
  requestData: EpgRequestArgs,
  externalIds: string[],
  allDayEpg = true,
  singleTimestamp = Date.now(),
): Observable<Record<string, KuxProgramType>> => {
  if (externalIds.length === 0) {
    return of({});
  }

  const filter = new KalturaSearchAssetFilter({
    kSql: allDayEpg
      ? epgMosaicArgsToKsql(requestData, externalIds)
      : epgMosaicArgsToKsqlSingle(requestData, externalIds, singleTimestamp),
    orderBy: 'START_DATE_ASC',
  });

  return getAssetsRecursively({
    filter,
    chunkSize: PERFORMANCE_FEATURE.PERFORMANCE_CHUNK_SIZE_EPG_PROGRAM,
  }).pipe(
    map((assetList: KalturaAssetListResponse) => {
      const assets = assetList.objects as KalturaProgramAsset[];
      const retrievedAssets = assets
        .filter(
          (asset) =>
            (asset.startDate <= requestData.endTime / 1000 && asset.startDate >= requestData.startTime / 1000) ||
            (asset.endDate <= requestData.endTime / 1000 && asset.endDate >= requestData.startTime / 1000),
        )
        .map((singleAsset) => assetToProgram(singleAsset as KalturaProgramAsset));

      return externalIds.reduce((acc: Record<string, KuxProgramType>, externalId) => {
        const program = retrievedAssets.find((asset) => asset.externalId === externalId);
        if (program) {
          acc[externalId] = program;
        }

        return acc;
      }, {});
    }),
  );
};

const mapMosaicPrograms = (
  assets: KuxProgramType[],
  mosaicProgramsMap: { [key: string]: KuxProgramType },
): KuxProgramType[] => {
  return assets.map((asset) => {
    if (asset.mosaicInfo?.isMosaicEvent === '1' && asset.mosaicInfo.mosaicProgramExternalId) {
      const mosaicProgram = cloneObject(mosaicProgramsMap[asset.mosaicInfo.mosaicProgramExternalId]);

      if (mosaicProgram) {
        if (mosaicProgram.mosaicInfo) {
          mosaicProgram.mosaicInfo.sourceLinearAssetId = asset.linearAssetId;
          mosaicProgram.permanentProgram = asset;
        } else {
          mosaicProgram.mosaicInfo = {
            isMosaicEvent: '1',
            sourceLinearAssetId: asset.linearAssetId,
          };
        }

        return mosaicProgram;
      }
    }

    return asset;
  });
};

/**
 * Return array with one current program
 */
const toSingleEpgOutput = (programs: KuxProgramType[], timestamp = Date.now()): KuxProgramType[] => {
  return programs.filter((program) => program.startDate <= timestamp && program.endDate >= timestamp);
};

/**
 * Loads EPG data
 */
export const getEpgDataV2 = (
  data: EpgRequestArgs,
  isSingle = false,
  singleTimestamp = Date.now(),
  allDayEpg = true,
  cancelSubject = new Subject(),
  maxEpgDateBackward = MAX_EPG_PERIOD,
  maxEpgDateForward = MAX_EPG_PERIOD,
  handleError?: boolean,
): Observable<KuxProgramType[]> => {
  if (
    startOfDay(new Date(Date.now()), -maxEpgDateBackward) > data.startTime ||
    data.startTime >= startOfDay(new Date(Date.now()), maxEpgDateForward)
  ) {
    // Do not use EPG for more than 7 days
    return of([]);
  }

  return from(getEpgCacheDays(data)).pipe(
    switchMap((epgCacheDays) => {
      const { cachedEpg, noCachedIdList } = epgCacheDays;
      if (noCachedIdList.length === 0) {
        return of(!isSingle ? cachedEpg : toSingleEpgOutput(cachedEpg, singleTimestamp));
      }
      const requestData = { ...data, linearIdList: noCachedIdList };

      const filter = new KalturaSearchAssetFilter({
        kSql: allDayEpg ? epgArgsToKsql(requestData) : epgArgsToKsqlSingle(requestData, singleTimestamp),
        orderBy: 'START_DATE_ASC',
      });

      return getAssetsRecursively({
        filter,
        chunkSize: PERFORMANCE_FEATURE.PERFORMANCE_CHUNK_SIZE_EPG_PROGRAM,
      }).pipe(
        map((assetList: KalturaAssetListResponse) => {
          const assets = assetList.objects as KalturaProgramAsset[];
          return assets
            .filter(
              (asset) =>
                (asset.startDate <= data.endTime / 1000 && asset.startDate >= data.startTime / 1000) ||
                (asset.endDate <= data.endTime / 1000 && asset.endDate >= data.startTime / 1000),
            )
            .map((singleAsset) => assetToProgram(singleAsset as KalturaProgramAsset));
        }),
        concatMap((assets) => {
          if (data.loadMosaicPrograms) {
            return getMosaicPrograms(
              data,
              // @ts-expect-error TODO Fix
              assets
                .filter(
                  (asset) =>
                    asset.mosaicInfo?.isMosaicEvent === '1' &&
                    asset.mosaicInfo.mosaicProgramExternalId &&
                    (asset.mosaicInfo.type === 'hidden' || asset.mosaicInfo.type === 'video'),
                )
                .map((asset) => asset.mosaicInfo?.mosaicProgramExternalId),
              allDayEpg,
              singleTimestamp,
            ).pipe(map((mosaicProgramsMap) => mapMosaicPrograms(assets, mosaicProgramsMap)));
          }

          return of(assets);
        }),
        map((assets) => {
          const { cachedPrograms: newCache, aggregateEpg } = aggregateEpgByDay(
            noCachedIdList,
            assets,
            data.startTime,
            data.loadMosaicPrograms,
          );

          if (isSingle) {
            if (allDayEpg) {
              setEpgCacheDays(aggregateEpg);
              return toSingleEpgOutput([...cachedEpg, ...newCache] as KuxProgramType[], singleTimestamp);
            }
            return toSingleEpgOutput([...cachedEpg, ...assets], singleTimestamp);
          }

          setEpgCacheDays(aggregateEpg);
          return [...cachedEpg, ...newCache];
        }),
        catchError((error) => {
          if (handleError) {
            throw error;
          }

          return of([]);
        }),
      );
    }),
    takeUntil(cancelSubject),
  );
};

export const findNotFoundForLinearId = (
  linearId: number,
  timestamp = Date.now(),
): Observable<undefined | KuxProgramType> => {
  const filterLeft = new KalturaSearchAssetFilter({
    kSql: epgArgsToKsqlProgramLeft(timestamp, linearId),
    orderBy: 'START_DATE_DESC',
  });

  const filterRight = new KalturaSearchAssetFilter({
    kSql: epgArgsToKsqlProgramRight(timestamp, linearId),
    orderBy: 'START_DATE_ASC',
  });

  return zip(
    getAssetList({
      filter: filterLeft,
      pager: new KalturaFilterPager({
        pageIndex: 1,
        pageSize: 1,
      }),
    }),
    getAssetList({
      filter: filterRight,
      pager: new KalturaFilterPager({
        pageIndex: 1,
        pageSize: 1,
      }),
    }),
  ).pipe(
    map(([leftProgramResponse, rightProgramResponse]: [KalturaAssetListResponse, KalturaAssetListResponse]) => {
      return getNotFoundProgramType(
        linearId,
        leftProgramResponse.objects?.length ? leftProgramResponse.objects[0].endDate * 1000 : 0,
        rightProgramResponse.objects?.length ? rightProgramResponse.objects[0].startDate * 1000 : 1000000000000000,
      );
    }),
    catchError((error) => {
      return EMPTY;
    }),
  );
};

export const findNotFoundForLinearIdList = (
  linearIdList: number[],
  timestamp = Date.now(),
): Observable<KuxProgramType[]> => {
  if (!linearIdList.length) {
    return of([]);
  }
  return zip(...linearIdList.map((linearId) => findNotFoundForLinearId(linearId, timestamp))).pipe(
    map((programs) => programs.filter(Boolean) as KuxProgramType[]),
  );
};

/**
 * Load data to epgCache.
 */
export const backgroundDownloadEpg = (epgData: EpgRequestArgs[], cancelSubject: Subject<unknown>, concurrent = 3) => {
  return of(...epgData).pipe(
    mergeMap((val) => getEpgDataV2(val, undefined, undefined, undefined, cancelSubject), concurrent),
    takeUntil(cancelSubject),
  );
};
